/**
*
* Conversations
*
*/

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Card } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { useWidth } from 'utils/checkMedia.js';
import useRecoveredData from 'utils/useRecoveredData.js';
import messages from './messages.js';
import conversationsImage from './conversations.png';
import ListMessages from './ListMessages/index.jsx';
import TopicHeader from './TopicHeader/index.jsx';
import AddMessage from './AddMessage.jsx';
import useConversations from './useConversations.js';
import Topics from './Topics/index.jsx';
import Img from './Img.js';
import TopicStatus from './TopicHeader/TopicStatus.jsx';
import { ConversationsContext } from './constants.jsx';
import ConversationsBackButton from './ConversationsBackButton.jsx';

const Wrapper = styled.div`
  min-height: 400px;
  & > div.card {
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    max-height: calc(100vh - 100px);
  }
  & > div.card:first-child {
    @media screen {
      flex: 0 0 40%;
      max-width: 40%;
      min-width: 20rem;
      @media (max-width: ${(props) => props.theme.screenXsMax}) {
        max-width: 100%;
      }
    }
  }
  .search-wrapper {
    width: auto;
    input {
      width: 12em;
    }
  } 
`;

function Conversations(props) {
  const { setMeta } = props;
  const { inboxId, topicId, subTopicId } = useParams();
  const [storedMessage] = useRecoveredData('topics', inboxId, 'msg', subTopicId || topicId, 'new');
  const [editMessage, setEditMessage] = useState(storedMessage);
  const topicsProps = useConversations();
  const { inbox, archive, query, topics, topic, parentTopic } = topicsProps;
  useEffect(() => {
    const title = [messages.conversations, topic?.title].filter(Boolean);
    setMeta({ title });
  }, [topic?.title]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectMessage = topics.list && (topics.list.length === 0 ? messages.conversationShowsHere : messages.selectConversation);
  const canLoadMessages = Boolean(topic?.id && topic !== parentTopic);
  const widerScreen = useWidth('md');
  const showTopics = widerScreen || !canLoadMessages;
  const showMessages = widerScreen || canLoadMessages;
  const postfix = parentTopic ? `/${parentTopic.id}` : '';

  return (
    <ConversationsContext.Provider value={topicsProps}>
      <Wrapper className="d-sm-flex pt-3 px-0 px-sm-3 pb-0">
        { showTopics && (
          <Card className={classNames(['me-sm-4 pb-2', { 'd-print-none': canLoadMessages }])}>
            <Topics topics={topics} topicId={topicId} parentTopic={parentTopic} topic={topic} archive={Boolean(archive)} />
          </Card>
        )}
        { showMessages && (
          <Card className={classNames(['flex-grow-1 d-flex flex-column', { 'd-print-none': !canLoadMessages }])}>
            {topic?.id && (
              <Card.Header>
                <div className="d-flex">
                  {!showTopics && <ConversationsBackButton postfix={postfix} />}
                  <TopicHeader topic={topic} markText={query} className="flex-grow-1" />
                </div>
                <TopicStatus topic={topic} />
              </Card.Header>
            )}
            {canLoadMessages ? (
              <>
                <ListMessages topic={topic} markText={query} creating={editMessage !== null} className="mb-4 overflow-auto" />
                <div className="flex-grow-1" />
                {!inbox.archived && (
                  <div className="px-4 d-print-none">
                    <AddMessage editMessage={editMessage} setEditMessage={setEditMessage} topic={topic} />
                  </div>
                )}
              </>
            ) : (
              <Card.Body className="flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                <Img src={conversationsImage} alt="" />
                <div className="text-large mt-4 mx-3 mx-sm-5 text-center">
                  {selectMessage && <FormattedMessage {...selectMessage} />}
                </div>
              </Card.Body>
            )}
          </Card>
        )}
      </Wrapper>
    </ConversationsContext.Provider>
  );
}

Conversations.propTypes = {
  match: PropTypes.object,
  setMeta: PropTypes.func,
};

export default Conversations;
